/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Box, IconButton, Tooltip} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ToggleFullScreenButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton, MRT_GlobalFilterTextField
} from "material-react-table";
import React, {useEffect, useMemo, useState} from "react";
import {usePostRequest} from "../../../../../api/helper";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import {tableButtonGray} from "../../../../../constants/colors";
import Add from "@mui/icons-material/Add";
import ArgosDialog from "../../../../components/Dialogs/ArgosDialog";
import AddVehicleProgramDialog from "./AddVehicleProgramDialog";
import {ArgosDeleteIcon, ArgosDownloadIcon, ArgosEditIcon} from "../../../../components/Icons/ArgosActionIcons";
import {useGetIsUserAdmin, useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {loginRequest, requestIdToken} from '../../../../../auth/authConfig';
import {useMsal} from '@azure/msal-react';
import {config} from "../../../../../AppConfig/config";
import {toast} from "react-hot-toast";
import {VEHICLE_PROGRAMS_CONSTANTS} from "../../../vehiclePrograms/constants";
import LaunchIcon from "@mui/icons-material/Launch";
import BoxWithCenteredContent from "../../../../components/Containers/BoxWithCenteredContent";
import ArgosButton, {ArgosLoadingButton} from "../../../../components/Buttons/ArgosButton";
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks";
import {
  selectAdasProgramsArchive,
  selectAdasProgramsState,
  selectIsVehicleProgramReadOnlyState,
  setIsVehicleProgramsReadOnly
} from "../../../../../redux/ducks/AdasPrograms/slice";
import ADAS_PROGRAMS_CONSTANTS from "../../constants";
import {selectCurrentUser} from "../../../../../redux/ducks/AccessControl/slice";
import {ArgosTableSimple} from "../../../../components/Table/ArgosTableSimple";

interface IProps {
  adasProgramId: string
}

const SENSOR_BOM_LIST_COLUMNS = [
  {header: 'Vehicle Program Code', id: 'programCode', size: 330,},
  {header: 'Model Year', id: 'modelYear', size: 330,},
  {header: 'Model Year Version', id: 'version', size: 330,},
  {header: 'Notes', id: 'notes', size: 400,},
]
const deleteDialogMessage="Are you sure you want to delete this Vehicle program record? This will delete all of its data. This action cannot be undone."
const VehicleProgramsTab = (props: IProps) => {
  const [isDeleteProgramLoading, setIsDeleteProgramLoading] = React.useState(false)
  const {adasProgramId} = props
  const reduxIsProgramArchive = useAppSelector(state => selectAdasProgramsArchive(state))
  const isReadOnlyProgram = useAppSelector(state => selectIsVehicleProgramReadOnlyState(state)) || reduxIsProgramArchive
  const [data, setData] = React.useState<any>([]);
  const [isTableLoading, setIsTableLoading] = useState(true)
  const columns: MRT_ColumnDef<Record<string, any>>[] = useMemo(() => SENSOR_BOM_LIST_COLUMNS.map((column) => {
    const col: MRT_ColumnDef<Record<string, any>> = {
      ...column,
      accessorKey: `${column.id}`,
    }
    return col
  }), [])

  const useGetHandleFetchIsVehicleProgramsReadOnly = () => {
    const dispatch = useAppDispatch()
    const handleCheckSensorBomReadOnly = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.isSensorBomReadOnly)
    //const handleGetVehIds = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.getVehIdsSensorBom)
    return (adasProgramId: string, gmid: string, isAdmin: boolean) => {
      if (adasProgramId && adasProgramId !== '') {
        handleCheckSensorBomReadOnly({
          "adasProgramId": adasProgramId,
          "currentUserGmid": gmid,
          "isAdmin": isAdmin,
          "isBoM" : false
        }).then(r => {
          dispatch(setIsVehicleProgramsReadOnly(r.isReadOnly))
        }).catch(e => {
          console.error(e)
        })
      }
    }
  }

  const handleCheckIsVehicleProgramsReadOnly = useGetHandleFetchIsVehicleProgramsReadOnly()
  const gmid = useAppSelector(state => selectCurrentUser(state).id)
  const isAdmin = useGetIsUserAdmin();
  useEffect( () =>{
    handleCheckIsVehicleProgramsReadOnly(adasProgramId, gmid, isAdmin)
  }, [props])


  const fetchVehicleProgramsHandle = usePostRequest('/HttpTriggerAdasPrograms?page=vehiclePrograms&requestType=mainTable')
  useEffect(() => {
    fetchData()
  }, []);

  const fetchData = () => {
    setIsTableLoading(true)
    fetchVehicleProgramsHandle({adasProgramId}).then((res) => {
      setIsTableLoading(false)
      setData(res)
    }).catch((err) => {
      setIsTableLoading(false)
      console.log(err)
    })
  }

  const [dialogState, setDialogState] = React.useState<any>(null)

  const editSensor = (_formData: any, rowIndex: number) => {
    const _dialogState = {
      formData: _formData,
      adasProgramId: adasProgramId,
      rowIndex: rowIndex,
      dialogType: 'edit'
    }
    setDialogState(_dialogState)
  }
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState<any>(null)
  const onDeleteProgram = (vehicleProgramId: string) => {
    // setOpenDeleteDialog(row?.original ?? null)

    setOpenDeleteDialog({initialLoad: true,vehicleProgramId:vehicleProgramId})
   
  }
  const deleteVehicleProgramHandle = usePostRequest('/HttpTriggerAdasPrograms?page=vehiclePrograms&requestType=deleteVehicleProgram')
  const deleteVehicleProgram = (vehicleProgramId: string) => {
    setIsDeleteProgramLoading(true);
    deleteVehicleProgramHandle({vehicleProgramId, adasProgramId}).then((res) => {
      if (res?.type === 'error') {
        setIsDeleteProgramLoading(false);
        toast.error(res?.message)
        return
      } else {
        setData(res)
        setIsDeleteProgramLoading(false);
        setOpenDeleteDialog(null)

      }
    }).catch((err) => {
      console.log(err)
      toast.error('Failed to delete vehicle program')
    })
  }

  const {instance, accounts} = useMsal()
  const downloadSheet = async (row: any) => {
    const {programCode, modelYear, notes, vehicleProgramId} = row
    const downloadRequest = {
      // programCode: programCode,
      // modelYear: modelYear,
      adasProgramId,
      vehicleProgramId
    }
    const fileName = `${adasProgramId}_${programCode}_${modelYear}_SensorPositions.xlsx` // # TODO: set this in backend instead
    let url = `${config.url.API_URL}/HttpTriggerDownloadFile?request=sensorPositionsSheet`
    const idTok = await requestIdToken(instance, accounts)
    fetch(url, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${idTok}`
      },
      body: JSON.stringify(downloadRequest)
    }).then((res) => {
      if (res.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
            console.log(e)
          }
        )
      res.blob().then((blob: any) => {
        const fileURL = window.URL.createObjectURL(blob)
        let alink = document.createElement('a')
        alink.href = fileURL;
        alink.download = fileName
        alink.click();
      })
    }).catch((e) => {
      console.log(e)
    })
  }

  const hasWriteAccess = useHasWriteAccess()
  return (
    <div>
      <ArgosTableSimple 
        data={data}
        columns={columns}

        state={{isLoading:isTableLoading}}
        renderRowActions={({row}: any) => tableRowActions(row, hasWriteAccess, onDeleteProgram, editSensor, downloadSheet, isReadOnlyProgram)}
        enableRowActions
        positionActionsColumn="last"
        displayColumnDefOptions={{
          'mrt-row-actions': {
            size: 200,
          },
        }}
        enableBottomToolbar={false}
      />
      <ArgosDialog
        maxWidth={'fit-content'} minWidth={'0px'} height={'fit-content'}
        open={dialogState != null} onClose={() => setDialogState(null)}
        headerTitle={'Add Vehicle Program'}
        headerTitleAlign={'left'}
      >
        <AddVehicleProgramDialog
          dialogState={dialogState}
          adasProgramId={adasProgramId}
          closeDialog={() => {
            // setAddVehicleProgramDialogOpen(false)
            setDialogState(null)
          }}
          onSaveComplete={() => {
            // TODO: maybe just add locally no network call (return data from backend and pass in here)
            setDialogState(null)
            fetchData()
          }}
          tableRows={data}
        />
      </ArgosDialog>
      <ArgosDialog open={openDeleteDialog != null} onClose={() => setOpenDeleteDialog(null)}
        width={'auto'} height={'auto'} headerTitle={'Delete Vehicle Program'} headerTitleAlign={'left'}>
        <BoxWithCenteredContent horizontal vertical
          style={{ height: '100%', display: 'flex', flexFlow: 'column nowrap' }}>
          <p style={{ width: '300px', textAlign: 'center' }}>{deleteDialogMessage}</p>
          <ArgosFlexbox>
            <ArgosButton
              // disabled={isDeleteProgramLoading && !initialDeleteDialogLoading} // TODO: If do this then dialog reopens.
              disabled={isDeleteProgramLoading}
              onClick={() => setOpenDeleteDialog(null)}>CANCEL</ArgosButton>
            <ArgosLoadingButton
              disabled={isDeleteProgramLoading}
              isLoading={isDeleteProgramLoading}
              onClick={() => {
                deleteVehicleProgram(openDeleteDialog.vehicleProgramId)
                //deleteProgram(openDeleteDialog?.data?.id)
              }}
            >
              DELETE
            </ArgosLoadingButton>
          </ArgosFlexbox>
        </BoxWithCenteredContent>
      </ArgosDialog>
    </div>
  )
}

const tableRowActions = (row: any, hasWriteAccess: boolean, onDeleteProgram: any, editSensor: any, downloadSheet: any, isReadOnlyProgram: boolean) => {
  const {programCode, modelYear, notes} = row.original
  const rowIndex = row.index

  const vehicleProgramId = row.original?.vehicleProgramId
  const url = VEHICLE_PROGRAMS_CONSTANTS.itemDetails.constructRouteUrl(vehicleProgramId)
  return (
    <Box
      sx={{
        display: row.subRows?.length ? 'none' : 'flex',
        flexWrap: 'nowrap'
      }}
    >
      <IconButton
        size={'large'}
        onClick={(e) => {
          e.preventDefault()
          window.open(url, '_blank')
        }}
      >
        <LaunchIcon/>
      </IconButton>
    </Box>
  )
}

export default VehicleProgramsTab